/**
 * AtlasMainMenuModule — the boot main menu.
 *
 * The #main-menu overlay (index.html, z above the loading screen, themed on the
 * live night sky + the two civilizations) covers everything from first paint.
 * NOTHING enters a world until the player chooses:
 *
 *   ENTER WORLD   → reveals the bottom WORLDS dock (nothing loads yet):
 *                     · THE COMMONS — the community world (explicit id when the
 *                       row has replicated; the deferred auto-enter otherwise)
 *                     · YOUR WORLD — the personal refuge seeded at signup
 *                     · worlds the player founded (owned, non-personal)
 *                     · a live search over OTHER players' public worlds
 *                   Picking one fires atlas:menu-enter { worldId } and only
 *                   then does AtlasLoadingModule start its readiness checks.
 *   CREATE WORLD  → spawnWorld (individual/persistent/solo), wait for the owned
 *                   row, apply visibility, then menu-enter { worldId }.
 *   SETTINGS      → graphics tier picker (quality_tiers), persisted — the ideal
 *                   moment to pick a tier: nothing GPU-heavy has loaded yet.
 *
 * First-run identity: the Passport kiosk (#login) must be usable, so while it
 * is shown this menu hides itself and returns on atlas:account-ready.
 * Harness/embed contexts without the overlay keep the old auto-enter flow
 * (the network module checks for #main-menu in the DOM).
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { beginJourney } from '../lib/journey.js';
import { TIER_ORDER, TIER_LABEL, activeTier, setActiveTier, saveSettings } from '../lib/quality_tiers.js';

/** The paintings dissolve to the next every ten seconds (user spec, 2026-09-08). */
export const SLIDE_MS = 10_000;

export class AtlasMainMenuModule implements IModule {
  readonly name = 'AtlasMainMenu';

  private dismissed = false;
  private pollTimer: number | null = null;
  private app: App | null = null;
  private linked = false;
  private mgRaf: number | null = null;

  init(app: App): void {
    this.app = app;
    const menu = document.getElementById('main-menu');
    if (!menu) return; // harness/embed: no menu — auto-enter flow stays active

    const enterBtn = document.getElementById('menu-enter') as HTMLButtonElement | null;
    const createBtn = document.getElementById('menu-create') as HTMLButtonElement | null;
    const settingsBtn = document.getElementById('menu-settings') as HTMLButtonElement | null;
    const status = document.getElementById('menu-status');

    // Loading bar + visible errors while the passport link comes up: the
    // buttons sit disabled until account-ready, and on a headset there is no
    // console — a silent connect failure looked like "the menu ignores me"
    // (Quest, 2026-08-03). The bar shimmers until linked; any connect
    // error/disconnect prints on the menu and turns the bar red; a watchdog
    // surfaces a hint when nothing has resolved after 20 s.
    const loadbar = document.getElementById('menu-loadbar');
    const errLine = document.getElementById('menu-error');
    const showErr = (text: string): void => {
      if (errLine) {
        errLine.textContent = text;
        errLine.classList.add('show');
      }
      loadbar?.classList.add('err');
    };
    bus.on('atlas:status', ({ text }) => {
      if (this.linked) return;
      if (/connect error|disconnected/i.test(text)) showErr(`${text} — check that the server is reachable, then reload.`);
    });
    const watchdog = window.setTimeout(() => {
      if (!this.linked) showErr('still connecting… the passport service has not answered yet (20 s). If this persists, reload or check the network.');
    }, 20_000);

    // Waiting mini-game (comet catch) — lives only while the menu gates.
    this.startMinigame();
    // The paintings: dissolve to the next every SLIDE_MS while the menu shows.
    this.startSlides();

    // Account link status: buttons unlock when the passport resolves.
    bus.on('atlas:account-ready', ({ username }) => {
      this.linked = true;
      window.clearTimeout(watchdog);
      if (enterBtn) enterBtn.disabled = false;
      if (createBtn) createBtn.disabled = false;
      if (status) status.textContent = `◈ passport linked — ${username}`;
      loadbar?.remove();
      errLine?.classList.remove('show');
      this.stopMinigame();
      document.getElementById('menu-minigame')?.classList.add('hidden');
      menu.style.display = ''; // return from a kiosk hand-off
    });

    // First-run: the Passport kiosk (#login.show) must be clickable — yield to it.
    const login = document.getElementById('login');
    if (login) {
      const sync = (): void => {
        if (this.dismissed) return;
        menu.style.display = login.classList.contains('show') ? 'none' : '';
      };
      new MutationObserver(sync).observe(login, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    // ── ENTER WORLD reveals the bottom WORLDS dock (design comp 2026-07-21).
    // NOTHING loads here — the dock only lists destinations; entry fires from
    // a card pick. Create/Settings stay hero-flow panels, mutually exclusive.
    const dock = document.getElementById('menu-worlds-dock');
    const createPanel = document.getElementById('menu-create-panel');
    const settingsPanel = document.getElementById('menu-settings-panel');
    enterBtn?.addEventListener('click', () => {
      createPanel?.classList.remove('open');
      settingsPanel?.classList.remove('open');
      this.buildWorldSelect();
      // The dock overlays the mini-game's screen region — retire the game the
      // moment destination browsing starts, or a missed comet tap hits a
      // world card and ENTERS it (Quest report, 2026-08-03).
      this.stopMinigame();
      document.getElementById('menu-minigame')?.classList.add('hidden');
      dock?.classList.add('show');
      (document.getElementById('menu-world-search') as HTMLInputElement | null)?.focus();
    });

    // Traveler screen hand-off: BACK un-parks the menu; the journey's start
    // (atlas:menu-enter, emitted by the traveler screen) dismisses it for good.
    bus.on('atlas:traveler-back', () => { menu.classList.remove('parked'); this.startSlides(); });
    bus.on('atlas:menu-enter', () => this.finalize());
    createBtn?.addEventListener('click', () => {
      createPanel?.classList.toggle('open');
      settingsPanel?.classList.remove('open');
    });
    settingsBtn?.addEventListener('click', () => {
      settingsPanel?.classList.toggle('open');
      createPanel?.classList.remove('open');
    });

    // ── World select wiring (static buttons + live search) ──
    document.getElementById('menu-enter-commons')?.addEventListener('click', () => {
      // Explicit id when the commons row is here; the deferred auto-enter
      // covers a slow first replication (it resolves the Commons server-side).
      this.enter(this.commonsId());
    });
    document.getElementById('menu-enter-personal')?.addEventListener('click', () => {
      const id = this.personalId();
      if (id !== null) this.enter(id);
    });
    document.getElementById('menu-world-search')?.addEventListener('input', () => this.buildFoundList());

    // ── Settings: graphics tier picker ──
    const tiers = document.getElementById('menu-tiers');
    if (tiers) {
      for (const t of TIER_ORDER) {
        const b = document.createElement('button');
        b.textContent = TIER_LABEL[t];
        b.dataset.tier = t;
        b.addEventListener('click', () => {
          setActiveTier(t);
          saveSettings();
          paintTiers();
          // Route through the FULL apply path (same as Esc/F4): the bare
          // quality-changed emit re-scaled shadows/culling but left pixelRatio,
          // bloom/SMAA, the clouds composite, and fogScale at the boot preset —
          // picking Potato at the main menu only half-applied (finding F5).
          const post = this.app?.getModule('Post') as { applySettings?: () => void } | undefined;
          if (post?.applySettings) post.applySettings();
          else bus.emit('atlas:quality-changed', {});
        });
        tiers.appendChild(b);
      }
      const paintTiers = (): void => {
        for (const b of Array.from(tiers.children) as HTMLButtonElement[]) {
          b.classList.toggle('active', b.dataset.tier === activeTier());
        }
      };
      paintTiers();
    }

    // ── Create World ──
    document.getElementById('menu-create-go')?.addEventListener('click', () => void this.createWorld());
  }

  // ── World select (the Enter World panel) ───────────────────────────────────

  /**
   * Comet-catch: a tiny self-contained canvas game for the passport wait.
   * Comets arc across the panel; a click/tap (mouse, touch, or the Quest
   * controller cursor) inside a comet's halo catches it. Pure DOM/canvas —
   * no engine coupling — and fully torn down on account-ready/dismiss.
   */
  private startMinigame(): void {
    const canvas = document.getElementById('mg-canvas') as HTMLCanvasElement | null;
    const scoreEl = document.getElementById('mg-score');
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    type Comet = { x: number; y: number; vx: number; vy: number; r: number; hue: number; caught: number };
    const comets: Comet[] = [];
    let score = 0;
    let last = performance.now();
    const spawn = (): void => {
      const fromLeft = Math.random() < 0.5;
      comets.push({
        x: fromLeft ? -20 : W + 20,
        y: 30 + Math.random() * (H - 80),
        vx: (fromLeft ? 1 : -1) * (60 + Math.random() * 90),
        vy: (Math.random() - 0.35) * 40,
        r: 9 + Math.random() * 9,
        hue: 190 + Math.random() * 90,
        caught: 0,
      });
    };
    spawn();
    const onTap = (ev: PointerEvent): void => {
      ev.stopPropagation();
      ev.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const px = ((ev.clientX - rect.left) / rect.width) * W;
      const py = ((ev.clientY - rect.top) / rect.height) * H;
      for (const c of comets) {
        if (!c.caught && Math.hypot(c.x - px, c.y - py) < c.r + 14) {
          c.caught = 1;
          score += 1;
          if (scoreEl) scoreEl.textContent = String(score);
          break;
        }
      }
    };
    canvas.addEventListener('pointerdown', onTap);
    const step = (now: number): void => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ctx.clearRect(0, 0, W, H);
      for (let i = comets.length - 1; i >= 0; i--) {
        const c = comets[i];
        if (c.caught) {
          // Catch burst: expand + fade, then remove.
          c.caught += dt * 6;
          ctx.globalAlpha = Math.max(0, 1.6 - c.caught);
          ctx.beginPath();
          ctx.arc(c.x, c.y, c.r * (1 + c.caught), 0, Math.PI * 2);
          ctx.strokeStyle = `hsl(${c.hue} 90% 70%)`;
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.globalAlpha = 1;
          if (c.caught > 1.6) comets.splice(i, 1);
          continue;
        }
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        // Tail
        const tail = ctx.createLinearGradient(c.x - c.vx * 0.4, c.y - c.vy * 0.4, c.x, c.y);
        tail.addColorStop(0, 'transparent');
        tail.addColorStop(1, `hsl(${c.hue} 90% 70% / 0.5)`);
        ctx.strokeStyle = tail;
        ctx.lineWidth = c.r * 0.8;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(c.x - c.vx * 0.4, c.y - c.vy * 0.4);
        ctx.lineTo(c.x, c.y);
        ctx.stroke();
        // Head
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${c.hue} 90% 78%)`;
        ctx.fill();
        if (c.x < -40 || c.x > W + 40 || c.y < -40 || c.y > H + 40) comets.splice(i, 1);
      }
      if (comets.length < 3 && Math.random() < 0.03) spawn();
      this.mgRaf = requestAnimationFrame(step);
    };
    this.mgRaf = requestAnimationFrame(step);
  }

  private stopMinigame(): void {
    if (this.mgRaf !== null) cancelAnimationFrame(this.mgRaf);
    this.mgRaf = null;
  }

  private commonsId(): bigint | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.worldId;
    return null;
  }

  private personalId(): bigint | null {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return null;
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'personal' && w.owner.isEqual(conn.identity)) return w.worldId;
    }
    return null;
  }

  /** Founder's username for a world row ('' when the account isn't replicated). */
  private founderOf(owner: { toHexString(): string; isEqual(o: unknown): boolean }): string {
    const conn = atlas.conn;
    if (!conn) return '';
    for (const a of conn.db.account.iter()) {
      if (a.identity.toHexString() === owner.toHexString()) return a.username;
    }
    return '';
  }

  /** One themed row in a world list. */
  private worldRow(name: string, meta: string, worldId: bigint): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'mw-row';
    const title = document.createElement('span');
    title.className = 'mw-title';
    title.textContent = name;
    const m = document.createElement('span');
    m.className = 'mw-meta';
    m.textContent = meta;
    const go = document.createElement('button');
    go.className = 'mw-go';
    go.textContent = 'ENTER';
    go.addEventListener('click', () => this.enter(worldId));
    row.append(title, m, go);
    return row;
  }

  /** (Re)build the whole panel: personal note, founded list, search results. */
  private buildWorldSelect(): void {
    const conn = atlas.conn;
    const personalBtn = document.getElementById('menu-enter-personal') as HTMLButtonElement | null;
    const personalSub = document.getElementById('menu-personal-sub');
    if (personalBtn) {
      const pid = this.personalId();
      personalBtn.disabled = pid === null;
      if (personalSub && pid === null) personalSub.textContent = 'your refuge is still being surveyed — a moment more';
    }
    const mine = document.getElementById('menu-my-worlds');
    if (mine && conn && conn.identity) {
      const rows: HTMLDivElement[] = [];
      for (const w of conn.db.world.iter()) {
        if (w.kind === 'commons' || w.kind === 'personal') continue;
        if (!w.owner.isEqual(conn.identity)) continue;
        rows.push(this.worldRow(w.name, `${w.sizeM} m · ${w.visibility}`, w.worldId));
      }
      mine.replaceChildren(...(rows.length > 0 ? rows : [this.emptyNote('none yet — found one with CREATE WORLD')]));
    }
    this.buildFoundList();
  }

  /** The public-worlds search: other players' public worlds, name/founder match. */
  private buildFoundList(): void {
    const conn = atlas.conn;
    const list = document.getElementById('menu-found-worlds');
    if (!list || !conn || !conn.identity) return;
    const q = ((document.getElementById('menu-world-search') as HTMLInputElement | null)?.value ?? '').trim().toLowerCase();
    const rows: HTMLDivElement[] = [];
    for (const w of conn.db.world.iter()) {
      if (rows.length >= 8) break;
      if (w.kind === 'commons' || w.kind === 'personal') continue;
      if (w.visibility !== 'public') continue;
      if (w.owner.isEqual(conn.identity)) continue; // mine live in the list above
      const founder = this.founderOf(w.owner);
      if (q && !w.name.toLowerCase().includes(q) && !founder.toLowerCase().includes(q)) continue;
      rows.push(this.worldRow(w.name, founder ? `by ${founder}` : `${w.sizeM} m`, w.worldId));
    }
    list.replaceChildren(...(rows.length > 0 ? rows : [this.emptyNote(
      q ? `no public world matches "${q}"` : 'public worlds founded by other travelers appear here',
    )]));
  }

  private emptyNote(text: string): HTMLDivElement {
    const d = document.createElement('div');
    d.className = 'mw-empty';
    d.textContent = text;
    return d;
  }

  /** Found a personal world, wait for its row, apply visibility, then enter it. */
  private async createWorld(): Promise<void> {
    const note = document.getElementById('menu-create-note');
    const say = (text: string, err = false): void => {
      if (note) { note.textContent = text; note.classList.toggle('err', err); }
    };
    const conn = atlas.conn;
    if (!conn || !conn.identity) { say('passport link not ready yet', true); return; }
    const name = (document.getElementById('menu-world-name') as HTMLInputElement | null)?.value.trim() ?? '';
    if (!name) { say('give the world a name first', true); return; }
    const sizeM = Number((document.getElementById('menu-world-size') as HTMLSelectElement | null)?.value ?? 64);
    const vis = (document.getElementById('menu-world-vis') as HTMLSelectElement | null)?.value ?? 'public';
    say(`founding "${name}"…`);
    try {
      await conn.reducers.spawnWorld({
        name, sizeM, kind: 'individual', lifecycle: 'persistent', governanceTemplate: 'solo', capacity: 32,
      });
    } catch (e) {
      say((e as Error).message, true); // e.g. insufficient SOV for the hosting fee
      return;
    }
    // The row replicates moments later — poll briefly for OUR world by name.
    const me = conn.identity;
    const started = performance.now();
    const find = (): bigint | null => {
      for (const w of conn.db.world.iter()) {
        if (w.name === name && w.owner.isEqual(me)) return w.worldId;
      }
      return null;
    };
    const wait = (): void => {
      const id = find();
      if (id !== null) {
        if (vis !== 'public') {
          void (conn.reducers as unknown as { setWorldVisibility(a: { worldId: bigint; visibility: string }): Promise<void> })
            .setWorldVisibility({ worldId: id, visibility: vis }).catch(() => { /* visibility is cosmetic here */ });
        }
        say('world founded — entering…');
        this.enter(id);
        return;
      }
      if (performance.now() - started > 10_000) { say('world row never arrived — try Enter World', true); return; }
      window.setTimeout(wait, 250);
    };
    wait();
  }

  /**
   * A world was picked. With the Traveler screen present, hand off to it
   * (nothing loads; BACK returns here) — its BEGIN JOURNEY begins the
   * journey (the intro, then atlas:menu-enter, which finalize() answers).
   * Without it (older embeds), begin the journey directly.
   */
  private enter(worldId: bigint | null): void {
    if (this.dismissed) return;
    const menu = document.getElementById('main-menu');
    if (document.getElementById('traveler-select')) {
      menu?.classList.add('parked');
      this.stopSlides();
      bus.emit('atlas:world-picked', { worldId });
      return;
    }
    beginJourney(worldId); // the intro gate forwards atlas:menu-enter (lib/journey.ts)
    this.finalize();
  }

  // ── The paintings (2026-09-08): public/images/home, one on at a time ──
  private slideTimer: number | null = null;
  private slideIdx = 0;
  private slideTicks = 0;
  private startSlides(): void {
    if (this.slideTimer !== null || this.dismissed) return;
    const slides = Array.from(document.querySelectorAll<HTMLImageElement>('#menu-slides .cm-slide'));
    if (slides.length < 2) return;
    // The next painting decodes ahead of its dissolve (a 400 KB WebP): warm it now.
    const warm = (i: number): void => { const img = slides[i % slides.length]; if (img.loading === 'lazy') img.loading = 'eager'; };
    warm(this.slideIdx + 1);
    this.slideTimer = window.setInterval(() => this.nextSlide(), SLIDE_MS);
    (globalThis as Record<string, unknown>).__menuSlides = () => ({
      count: slides.length, index: this.slideIdx, ticks: this.slideTicks, running: this.slideTimer !== null, periodMs: SLIDE_MS,
      on: slides.findIndex((el) => el.classList.contains('is-on')),
      next: () => this.nextSlide(),
    });
  }
  private nextSlide(): void {
    const slides = Array.from(document.querySelectorAll<HTMLImageElement>('#menu-slides .cm-slide'));
    if (slides.length < 2) return;
    slides[this.slideIdx % slides.length].classList.remove('is-on');
    this.slideIdx = (this.slideIdx + 1) % slides.length;
    slides[this.slideIdx].classList.add('is-on');
    const nxt = slides[(this.slideIdx + 1) % slides.length];
    if (nxt.loading === 'lazy') nxt.loading = 'eager';
    this.slideTicks++;
  }
  private stopSlides(): void {
    if (this.slideTimer !== null) { window.clearInterval(this.slideTimer); this.slideTimer = null; }
  }

  /** The journey began (traveler screen or direct) — fade the menu out for good. */
  private finalize(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.stopSlides();
    this.stopMinigame();
    if (this.pollTimer !== null) window.clearInterval(this.pollTimer);
    const menu = document.getElementById('main-menu');
    if (menu) {
      menu.classList.add('gone');
      window.setTimeout(() => menu.remove(), 700);
    }
  }
}
